/**
 * Which door checks the code — and when the honest answer is "neither".
 *
 * 🔴 THE BUG THIS EXISTS TO CLOSE MADE EVERY SIGN-IN FAIL WITH A LIE. On a
 * Firebase build the code is sent by Google and confirmed by Google;
 * `POST /auth/parent/otp/request` is never called, so no `OtpChallenge` row is
 * ever written. `AppRouter` chose the rail like this:
 *
 *     hasPendingCode(phone) ? confirmPhoneCode(code) : verifyParentOtp(phone, code)
 *
 * — so the moment the Firebase handle was missing, it posted the code to the
 * WhatsApp endpoint instead. That endpoint looked for a challenge that could not
 * exist, and answered its deliberately generic 401. The parent read *"that code
 * did not work — it may be wrong, or it may have expired"*, retried, and burned
 * the ten-a-minute IP limit into a 429.
 *
 * ⚠️ THE FALLBACK IS NOT WRONG IN PRINCIPLE, WHICH IS WHY IT SURVIVES BELOW. A
 * build genuinely can be switched to Firebase while a parent still holds a live
 * WhatsApp challenge from before the deploy, and confirming that code against
 * Google would refuse a code that is real. What was wrong is taking that branch
 * when there is nothing on the other side of it either.
 *
 * 🔴 SO THE THIRD ANSWER IS THE FIX: *"neither rail can check this"* is a state,
 * not a refusal. It happens for one ordinary reason — the confirmation handle
 * lives in module state and a reload drops it (`firebasePhone.ts` explains why
 * persisting it would be a credential at rest) — and the action it needs is
 * "ask for a new code", which is the one thing the generic 401 never said.
 */
export type OtpRail =
  /** Google sent it and Google checks it. */
  | "firebase"
  /** We sent it and we check it. */
  | "whatsapp"
  /**
   * Nobody can check it. Not a wrong code — no live challenge on either rail,
   * so every request would be refused whatever the parent typed.
   */
  | "expired";

export interface RailInput {
  /** Does this build have a Firebase project to send through? */
  firebaseConfigured: boolean;
  /** Is there a live Firebase confirmation for THIS number? */
  hasPendingCode: boolean;
}

/**
 * Pick the rail, or say that neither can answer.
 *
 * ⚠️ IT ASKS ABOUT THE HANDLE FIRST, NOT ABOUT THE CONFIG. A build configured
 * for Firebase can still be holding a WhatsApp challenge, and a build with
 * Firebase switched off can still be holding a Firebase handle from before the
 * env changed under a running tab. The handle is the fact; the config is only
 * what happens next time.
 */
export function railFor({ firebaseConfigured, hasPendingCode }: RailInput): OtpRail {
  if (hasPendingCode) return "firebase";
  /*
   * 🔴 NO HANDLE ON A FIREBASE BUILD MEANS NO CODE WAS SENT THROUGH A RAIL WE
   * CAN CHECK. `requestParentOtp` is not called on these builds, so posting to
   * the WhatsApp verifier is guaranteed to find nothing and guaranteed to blame
   * the parent's typing for it.
   */
  if (firebaseConfigured) return "expired";
  return "whatsapp";
}

/**
 * Should this code be sent at all?
 *
 * 🔴 RESUBMITTING A CODE THE SERVER HAS ALREADY REFUSED CANNOT SUCCEED, AND IT
 * IS NOT FREE. Each attempt spends one of the five a challenge allows and one of
 * the ten a minute the IP allows — so a parent tapping "Continue" again after a
 * refusal is spending the budget that would have covered the code they are about
 * to type correctly. The observed failure was ten verifies in four seconds, of
 * which nine could only ever have been refused.
 *
 * ⚠️ IT COMPARES THE CODE, NOT THE CLOCK. A cooldown would block a parent who
 * spotted their typo immediately, which is the fastest correct retry there is.
 * Changing a single digit re-enables the button at once.
 */
export function mayResubmit(code: string, lastRefused: string | null): boolean {
  if (code.length !== 6) return false;
  return code !== lastRefused;
}
